import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import VisaoEquipe from './VisaoEquipe';
import VisaoVendas from './VisaoVendas';
import type { TipoNotificar } from './utils';

interface Props {
  eventoId: number;
  eventoNome: string;
  /** Dentro do hub "Dia do evento" cada visão abre sozinha, sem o seletor. */
  visaoFixa?: 'equipe' | 'vendas';
}

interface Aviso {
  id: number;
  msg: string;
  tipo: TipoNotificar;
}

const ESTILO_AVISO: Record<TipoNotificar, string> = {
  ok: 'bg-success-600 text-white',
  erro: 'bg-danger-600 text-white',
  aviso: 'bg-warning-500 text-foreground-950',
};

const ICONE_AVISO: Record<TipoNotificar, string> = {
  ok: 'ri-checkbox-circle-line',
  erro: 'ri-error-warning-line',
  aviso: 'ri-alert-line',
};

// A aba "Equipe do dia": quem faz o quê no evento + as vendas do dia.
export default function EquipeDoDia({ eventoId, eventoNome, visaoFixa }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const visao = visaoFixa ?? (searchParams.get('visao') === 'vendas' ? 'vendas' : 'equipe');

  const [aviso, setAviso] = useState<Aviso | null>(null);
  const [convitesPendentes, setConvitesPendentes] = useState(0);

  useEffect(() => {
    if (!aviso) return;
    const t = setTimeout(() => setAviso(null), 4000);
    return () => clearTimeout(t);
  }, [aviso]);

  const notificar = useCallback((msg: string, tipo: TipoNotificar = 'ok') => {
    setAviso({ id: Date.now(), msg, tipo });
  }, []);

  function trocar(nova: 'equipe' | 'vendas') {
    const prox = new URLSearchParams(searchParams);
    if (nova === 'equipe') prox.delete('visao');
    else prox.set('visao', 'vendas');
    setSearchParams(prox, { replace: true });
  }

  return (
    <div className="space-y-4">
      {/* Seletor: Equipe · Vendas (some quando a visão é fixa) */}
      {!visaoFixa && (
      <div className="flex rounded-full bg-white p-1 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
        <button
          type="button"
          onClick={() => trocar('equipe')}
          className={`relative flex min-h-[44px] flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full text-[14px] font-semibold transition-colors ${
            visao === 'equipe' ? 'bg-primary-500 text-white' : 'text-foreground-700 hover:bg-primary-50'
          }`}
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-team-line" />
          </span>
          Equipe
          {convitesPendentes > 0 && (
            <span
              className={`ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-bold ${
                visao === 'equipe' ? 'bg-white text-primary-700' : 'bg-warning-500 text-foreground-950'
              }`}
            >
              {convitesPendentes}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => trocar('vendas')}
          className={`flex min-h-[44px] flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full text-[14px] font-semibold transition-colors ${
            visao === 'vendas' ? 'bg-primary-500 text-white' : 'text-foreground-700 hover:bg-primary-50'
          }`}
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-line-chart-line" />
          </span>
          Vendas
        </button>
      </div>
      )}

      {visao === 'equipe' ? (
        <VisaoEquipe
          eventoId={eventoId}
          eventoNome={eventoNome}
          aoContagem={setConvitesPendentes}
          notificar={notificar}
        />
      ) : (
        <VisaoVendas eventoId={eventoId} />
      )}

      {aviso && (
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-[60] flex justify-center px-4">
          <div
            className={`pointer-events-auto flex max-w-md items-center gap-2 rounded-2xl px-4 py-3 text-[14px] font-semibold shadow-lg ${ESTILO_AVISO[aviso.tipo]}`}
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center">
              <i className={ICONE_AVISO[aviso.tipo]} />
            </span>
            {aviso.msg}
          </div>
        </div>
      )}
    </div>
  );
}